import { useState } from 'react';
import { Eye, EyeOff, Reply, MessageSquare, MessageCircle } from 'lucide-react';
import { usePancakeStore } from '../../store/pancake.store';
import { platformBadge } from './PancakeDashboardFilters';
import ConversationDetail from './ConversationDetail';
import type { PancakeConversation } from '../../types/pancake';

type FilterType = 'all' | 'INBOX' | 'COMMENT';

export default function ConversationsList({ isDark }: { isDark: boolean }) {
  const { conversations, fetchConversationMessages } = usePancakeStore();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [selectedConversation, setSelectedConversation] = useState<PancakeConversation | null>(null);

  const filtered = filterType === 'all'
    ? conversations
    : conversations.filter((c) => c.type === filterType);

  const handleRowClick = async (conv: PancakeConversation) => {
    setSelectedConversation(conv);
    await fetchConversationMessages(conv._pageId, conv.id);
  };

  const typeBadge = (type: string) => {
    if (type === 'INBOX') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-300">
          <MessageSquare className="w-3 h-3" />
          Inbox
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-300">
        <MessageCircle className="w-3 h-3" />
        Comment
      </span>
    );
  };

  const filterBtnClass = (active: boolean) =>
    `px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
      active
        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
        : isDark
          ? 'bg-white/[0.03] border-white/10 text-white/40 hover:text-white hover:bg-white/[0.06]'
          : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-100'
    }`;

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-2">
        <span className={`text-xs mr-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Lọc:</span>
        <button onClick={() => setFilterType('all')} className={filterBtnClass(filterType === 'all')}>
          Tất cả ({conversations.length})
        </button>
        <button onClick={() => setFilterType('INBOX')} className={filterBtnClass(filterType === 'INBOX')}>
          Inbox ({conversations.filter((c) => c.type === 'INBOX').length})
        </button>
        <button onClick={() => setFilterType('COMMENT')} className={filterBtnClass(filterType === 'COMMENT')}>
          Comment ({conversations.filter((c) => c.type === 'COMMENT').length})
        </button>
      </div>

      {/* Table */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Từ</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Nội dung</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Loại</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Trang</th>
                <th className={`text-left px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Nền tảng</th>
                <th className={`text-center px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Đã xem</th>
                <th className={`text-center px-4 py-3 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Đã trả lời</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-100'}`}>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className={`px-4 py-8 text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Không có hội thoại nào
                  </td>
                </tr>
              )}
              {filtered.map((conv) => (
                <tr
                  key={conv.id}
                  onClick={() => handleRowClick(conv)}
                  className={`cursor-pointer ${isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <td className={`px-4 py-3 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {conv.from?.name || (conv.from as any)?.username || 'N/A'}
                  </td>
                  <td className={`px-4 py-3 max-w-xs truncate ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {conv.snippet || '—'}
                  </td>
                  <td className="px-4 py-3">{typeBadge(conv.type)}</td>
                  <td className={`px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{conv._pageName}</td>
                  <td className="px-4 py-3">{platformBadge(conv._platform)}</td>
                  <td className="px-4 py-3 text-center">
                    {conv.seen ? (
                      <Eye className={`w-4 h-4 mx-auto ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                    ) : (
                      <EyeOff className={`w-4 h-4 mx-auto ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {conv.is_replied ? (
                      <Reply className={`w-4 h-4 mx-auto ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                    ) : (
                      <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-300'}`}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Conversation detail modal */}
      {selectedConversation && (
        <ConversationDetail
          isOpen={!!selectedConversation}
          onClose={() => setSelectedConversation(null)}
          conversation={selectedConversation}
          isDark={isDark}
        />
      )}
    </div>
  );
}
